import React from "react";
import GroupCoverPositioner from "../../components/Groups/GroupCoverPositioner";
import GroupDetailModal from "../../components/Groups/GroupDetailModal";
import GroupPeopleModal from "../../components/Groups/GroupPeopleModal";
import { WAY_OF_FISHING_OPTIONS } from "../../utils/catchMetadataOptions";
import {
  GROUP_POSTING_APPROVAL_MODE_OPTIONS,
  GROUP_WATER_TYPE_OPTIONS,
  getGroupCoverScale,
  getGroupTypeOfFishingLabel,
  GroupActionButtons,
  GroupInvitePanel,
  GroupMemberList,
  GroupOverviewStats,
  GroupStatusBadges,
  resolveGroupCoverObjectPosition,
  resolveGroupCoverUrl,
} from "./GroupsSharedUi";

export default function GroupsModalLayer({ controller, t }) {
  const peopleListModal = controller.peopleListGroup ? (
    <GroupPeopleModal
      group={controller.peopleListGroup}
      title={controller.peopleListTitle}
      count={controller.peopleListIds.length}
      countLabel={controller.peopleListCountLabel}
      closeLabel={t("close")}
      onClose={controller.closePeopleList}
    >
      <>
        {controller.groupMemberActionError ? (
          <p className="groups-action-error">{controller.groupMemberActionError}</p>
        ) : null}
        <GroupMemberList
          group={controller.peopleListGroup}
          memberIds={controller.peopleListIds}
          showAdminActions
          controller={controller}
          t={t}
        />
      </>
    </GroupPeopleModal>
  ) : null;

  const selectedGroupRoleMessage = controller.selectedGroup
    ? controller.isAdmin(controller.selectedGroup)
      ? t("groups_role_admin_message")
      : controller.isMember(controller.selectedGroup)
        ? t("groups_role_member_message")
        : controller.hasPendingJoinRequest(controller.selectedGroup)
          ? t("groups_role_pending_message")
          : t("groups_role_guest_message")
    : "";

  const selectedGroupDetailModal = controller.selectedGroup ? (
    <GroupDetailModal
      group={controller.selectedGroup}
      closeLabel={t("close")}
      description={
        controller.selectedGroup.description || t("groups_no_description")
      }
      coverImageUrl={resolveGroupCoverUrl(controller.selectedGroup.coverImage)}
      coverImageAlt={controller.selectedGroup.name}
      coverObjectPosition={resolveGroupCoverObjectPosition(controller.selectedGroup)}
      coverScale={getGroupCoverScale(controller.selectedGroup)}
      coverInteractive={controller.canOpenGroupCover(controller.selectedGroup)}
      headerBadges={
        <GroupStatusBadges
          group={controller.selectedGroup}
          controller={controller}
          t={t}
        />
      }
      stats={
        <GroupOverviewStats
          group={controller.selectedGroup}
          controller={controller}
          t={t}
        />
      }
      roleMessage={selectedGroupRoleMessage}
      actions={
        <GroupActionButtons
          group={controller.selectedGroup}
          controller={controller}
          t={t}
          detailMode
        />
      }
      invitePanel={
        <GroupInvitePanel
          group={controller.selectedGroup}
          controller={controller}
          t={t}
        />
      }
      memberListTitle={t("groups_member_list")}
      memberCount={controller.selectedGroup.members.length}
      memberCountLabel={
        controller.selectedGroup.members.length === 1
          ? t("groups_member_count")
          : t("groups_members")
      }
      onCoverClick={() => controller.handleOpenGroupCover(controller.selectedGroup)}
      onClose={controller.closeGroupDetail}
    >
      <GroupMemberList group={controller.selectedGroup} controller={controller} t={t} />
    </GroupDetailModal>
  ) : null;

  const coverViewerModal =
    controller.fullCoverGroup &&
    controller.canOpenGroupCover(controller.fullCoverGroup) ? (
      <div
        className="groups-cover-viewer-overlay"
        onClick={controller.closeGroupCoverViewer}
        role="presentation"
      >
        <div
          className="groups-cover-viewer-modal"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="groups-cover-viewer-toolbar">
            <button
              type="button"
              className="groups-secondary-btn"
              onClick={controller.closeGroupCoverViewer}
            >
              {t("close")}
            </button>
            {controller.isAdmin(controller.fullCoverGroup) ? (
              <button
                type="button"
                className="groups-primary-btn"
                onClick={controller.openGroupCoverUpdatePicker}
              >
                {t("update_cover_image")}
              </button>
            ) : null}
          </div>

          <img
            src={resolveGroupCoverUrl(controller.fullCoverGroup.coverImage)}
            alt={controller.fullCoverGroup.name}
            className="groups-cover-viewer-image"
          />
        </div>
      </div>
    ) : null;

  const coverUpdateModal =
    controller.groupCoverUpdateTarget && controller.groupCoverUpdatePreviewUrl ? (
      <div
        className="groups-cover-edit-overlay"
        onClick={controller.resetGroupCoverUpdateState}
        role="presentation"
      >
        <div
          className="groups-cover-edit-modal"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <h3 className="groups-cover-edit-title">{t("update_cover_image")}</h3>
          <GroupCoverPositioner
            imageSrc={controller.groupCoverUpdatePreviewUrl}
            alt={t("groups_cover_preview_alt")}
            positionX={controller.groupCoverUpdatePosition.x}
            positionY={controller.groupCoverUpdatePosition.y}
            scale={controller.groupCoverUpdatePosition.scale}
            hintLabel={t("drag_to_reposition")}
            onPositionChange={controller.setGroupCoverUpdatePosition}
          />
          {controller.groupCoverUpdateError ? (
            <p className="groups-action-error">
              {controller.groupCoverUpdateError}
            </p>
          ) : null}
          <div className="groups-form-actions">
            <button
              type="button"
              className="groups-secondary-btn"
              onClick={controller.resetGroupCoverUpdateState}
              disabled={controller.groupCoverUpdateLoading}
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              className="groups-primary-btn"
              onClick={controller.handleSaveUpdatedGroupCover}
              disabled={controller.groupCoverUpdateLoading}
            >
              {controller.groupCoverUpdateLoading ? t("groups_updating") : t("save")}
            </button>
          </div>
        </div>
      </div>
    ) : null;

  const metadataEditModal = controller.groupMetadataEditTarget ? (
    <div
      className="groups-group-edit-overlay"
      onClick={() => controller.closeGroupMetadataEditor()}
      role="presentation"
    >
      <div
        className="groups-group-edit-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="groups-group-edit-title"
      >
        <button
          type="button"
          className="groups-detail-close"
          onClick={() => controller.closeGroupMetadataEditor()}
          aria-label={t("close")}
        >
          &times;
        </button>

        <h3 id="groups-group-edit-title" className="groups-group-edit-title">
          {t("groups_edit_metadata", { defaultValue: "Edit group details" })}
        </h3>
        <p className="groups-group-edit-subtitle">
          {controller.groupMetadataEditTarget.name}
        </p>

        <form onSubmit={controller.handleSaveGroupMetadata}>
          <div className="groups-form-row">
            <label htmlFor="group-edit-water-type">
              {t("groups_water_type", { defaultValue: "Water type" })}
            </label>
            <select
              id="group-edit-water-type"
              value={controller.groupMetadataEditWaterType}
              onChange={(event) =>
                controller.setGroupMetadataEditWaterType(event.target.value)
              }
            >
              {GROUP_WATER_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === "all"
                    ? t("all", { defaultValue: option.defaultLabel })
                    : t(`groups_water_type_${option.value}`, {
                        defaultValue: option.defaultLabel,
                      })}
                </option>
              ))}
            </select>
          </div>

          <div className="groups-form-row">
            <label htmlFor="group-edit-way-of-fishing">
              {t("catch_way_of_fishing", { defaultValue: "Way of Fishing" })}
            </label>
            <select
              id="group-edit-way-of-fishing"
              value={controller.groupMetadataEditWayOfFishing}
              onChange={(event) =>
                controller.handleGroupMetadataEditWayOfFishingChange(
                  event.target.value
                )
              }
            >
              <option value="all">{t("all", { defaultValue: "All" })}</option>
              {WAY_OF_FISHING_OPTIONS.map((way) => (
                <option key={way} value={way}>
                  {way === "Shore"
                    ? t("fishing_way_shore", { defaultValue: way })
                    : way === "Boat"
                      ? t("fishing_way_boat", { defaultValue: way })
                      : way}
                </option>
              ))}
            </select>
          </div>

          <div className="groups-form-row">
            <label htmlFor="group-edit-type-of-fishing">
              {t("catch_type_of_fishing", { defaultValue: "Type of Fishing" })}
            </label>
            <select
              id="group-edit-type-of-fishing"
              value={controller.groupMetadataEditTypeOfFishing}
              onChange={(event) =>
                controller.setGroupMetadataEditTypeOfFishing(event.target.value)
              }
            >
              <option value="all">{t("all", { defaultValue: "All" })}</option>
              {controller.groupMetadataEditTypeOfFishingOptions.map((type) => (
                <option key={type} value={type}>
                  {getGroupTypeOfFishingLabel(t, type)}
                </option>
              ))}
            </select>
          </div>

          <div className="groups-form-row">
            <label htmlFor="group-edit-rules">
              {t("groups_rules_label", { defaultValue: "Group rules" })}
            </label>
            <textarea
              id="group-edit-rules"
              value={controller.groupMetadataEditRules}
              onChange={(event) =>
                controller.setGroupMetadataEditRules(event.target.value)
              }
              maxLength={4000}
              placeholder={t("groups_rules_placeholder", {
                defaultValue: "Share any group rules or posting expectations.",
              })}
            />
          </div>

          <div className="groups-form-row">
            <label htmlFor="group-edit-posting-approval-mode">
              {t("groups_posting_approval_label", {
                defaultValue: "Posting approval",
              })}
            </label>
            <select
              id="group-edit-posting-approval-mode"
              value={controller.groupMetadataEditPostingApprovalMode}
              onChange={(event) =>
                controller.setGroupMetadataEditPostingApprovalMode(
                  event.target.value
                )
              }
            >
              {GROUP_POSTING_APPROVAL_MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`groups_posting_approval_${option.value}`, {
                    defaultValue: option.defaultLabel,
                  })}
                </option>
              ))}
            </select>
          </div>

          {controller.groupMetadataEditError ? (
            <p className="groups-form-error">{controller.groupMetadataEditError}</p>
          ) : null}

          <div className="groups-form-actions">
            <button
              type="button"
              className="groups-secondary-btn"
              onClick={() => controller.closeGroupMetadataEditor()}
              disabled={controller.groupMetadataEditLoading}
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              className="groups-primary-btn"
              disabled={controller.groupMetadataEditLoading}
            >
              {controller.groupMetadataEditLoading ? t("groups_updating") : t("save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : null;

  const confirmModal = controller.confirmAction && controller.confirmGroup ? (
    <div
      className="groups-confirm-overlay"
      onClick={() => controller.closeConfirmModal()}
      role="presentation"
    >
      <div
        className="groups-confirm-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="groups-confirm-title"
      >
        <h3 id="groups-confirm-title" className="groups-confirm-title">
          {controller.confirmTitle}
        </h3>
        <p className="groups-confirm-text">{controller.confirmMessage}</p>
        {controller.confirmError ? (
          <p className="groups-action-error">{controller.confirmError}</p>
        ) : null}
        <div className="groups-confirm-actions">
          <button
            type="button"
            className="groups-confirm-cancel"
            onClick={() => controller.closeConfirmModal()}
            disabled={controller.confirmLoading}
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            className={
              controller.confirmAction.type === "delete" ||
              controller.confirmAction.type === "removeMember"
                ? "groups-confirm-yes groups-confirm-danger"
                : "groups-confirm-yes"
            }
            onClick={controller.handleConfirmAction}
            disabled={controller.confirmLoading}
          >
            {controller.confirmLoading
              ? t("groups_updating")
              : controller.confirmAction.type === "delete" ||
                  controller.confirmAction.type === "removeMember"
                ? t("delete")
                : t("yes")}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <input
        ref={controller.groupCoverUpdateInputRef}
        type="file"
        accept="image/*"
        className="groups-cover-input"
        onChange={controller.handleGroupCoverUpdateChange}
      />
      {peopleListModal}
      {selectedGroupDetailModal}
      {coverViewerModal}
      {coverUpdateModal}
      {metadataEditModal}
      {confirmModal}
    </>
  );
}
